import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';

import useLoadWatchPlan from '../../hooks/useLoadWatchPlan';
import { RootState } from '../../store';
import { isMobile } from '../../store/screen/selectors';
import { resolveWatchPlanId, resolveWatchPlanPath } from '../../utils/api/getWatchPlan';
import LoadingIndicator from '../LoadingIndicator';
import WatchPlanDayList from './WatchPlanDayList';
import WatchPlanMain from './WatchPlanMain';
import WatchPlanResources from './WatchPlanResources';
import styles from './styles.module.css';

interface RouteParams {
  code?: string;
  slug?: string;
}

/**
 * Watch plan page: pretty slug URL for desktop and mobile.
 * Legacy /plan-video?plan=id redirects to the pretty path so the id stays out of the address bar.
 */
const WatchPlanPage = (): JSX.Element => {
  const history = useHistory();
  const location = useLocation();
  const { code, slug } = useParams<RouteParams>();
  const isPlanVideoRoute = useRouteMatch('/plan-video') !== null;

  const darkMode = useSelector((state: RootState) => state.settings.darkMode);
  const isMobileDevice = useSelector(isMobile);

  const planFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('plan') ?? undefined;
  }, [location.search]);

  const prettyPathFromPlanVideo = useMemo(() => {
    if (!isPlanVideoRoute || planFromQuery === undefined) {
      return undefined;
    }

    return resolveWatchPlanPath(planFromQuery);
  }, [isPlanVideoRoute, planFromQuery]);

  const watchPlanId = useMemo(() => {
    if (isPlanVideoRoute) {
      // Prefer rewriting to the pretty path; only load by id when unmapped.
      return prettyPathFromPlanVideo === undefined ? planFromQuery : undefined;
    }

    if (code !== undefined && slug !== undefined) {
      return resolveWatchPlanId(code, slug);
    }

    return undefined;
  }, [isPlanVideoRoute, prettyPathFromPlanVideo, planFromQuery, code, slug]);

  // Legacy App Link URL with Salesforce id → pretty slug path.
  useEffect(() => {
    if (prettyPathFromPlanVideo !== undefined) {
      history.replace(prettyPathFromPlanVideo);
    }
  }, [prettyPathFromPlanVideo, history]);

  const { plan, loading, error } = useLoadWatchPlan(watchPlanId);

  const sortedDays = useMemo(() => {
    if (plan === undefined) {
      return [];
    }

    return [...plan.days].sort((a, b) => a.day_order - b.day_order);
  }, [plan]);

  const [selectedDayId, setSelectedDayId] = useState<string>('');

  useEffect(() => {
    if (sortedDays.length > 0) {
      setSelectedDayId(sortedDays[0].id);
    } else {
      setSelectedDayId('');
    }
  }, [sortedDays]);

  const selectedDay = sortedDays.find(day => day.id === selectedDayId) ?? sortedDays[0];

  const outerClass = `${styles.outerContainer} ${darkMode && styles.outerContainerDarkMode}`;
  const statusClass = `${styles.statusMessage} ${darkMode && styles.statusMessageDarkMode}`;

  if (prettyPathFromPlanVideo !== undefined) {
    return (
      <div className={outerClass}>
        <LoadingIndicator size={120} />
      </div>
    );
  }

  if (watchPlanId === undefined || error) {
    return (
      <div className={outerClass}>
        <p className={statusClass}>This watch plan could not be found.</p>
      </div>
    );
  }

  if (loading || plan === undefined) {
    return (
      <div className={outerClass}>
        <LoadingIndicator size={120} />
      </div>
    );
  }

  if (sortedDays.length === 0 || selectedDay === undefined) {
    return (
      <div className={outerClass}>
        <p className={statusClass}>This watch plan has no days yet.</p>
      </div>
    );
  }

  const dayList = (
    <WatchPlanDayList
      days={sortedDays}
      selectedDayId={selectedDay.id}
      onSelectDay={setSelectedDayId}
      darkMode={darkMode}
      isMobile={isMobileDevice}
      showResources={false}
    />
  );

  return (
    <div className={outerClass}>
      <div className={`${styles.content} ${isMobileDevice && styles.contentMobile}`}>
        {!isMobileDevice && dayList}
        <WatchPlanMain
          plan={plan}
          selectedDay={selectedDay}
          darkMode={darkMode}
          isMobile={isMobileDevice}
          translationCode={code}
          dayList={isMobileDevice ? dayList : undefined}
          resources={<WatchPlanResources darkMode={darkMode} />}
        />
      </div>
    </div>
  );
};

export default WatchPlanPage;
